'use client';
import Button from '@ui/Button';
import Tooltip from '@ui/Tooltip';
import React from 'react';
import { HiHeart } from 'react-icons/hi';
import useUserProfile from '@/hooks/useUserProfile';
import { hasAppAccess } from '@/stores/userActions';
import { AppPreferenceItem } from '@/utils/appPreference';
import { differenceInDays } from 'date-fns';
import useHasUserSession from '@/hooks/useHasUserSession';
import useDonateDialogStore from '@/components/store/donateDialogSlice';
import { getUserCountry } from '@/utils/user.helper';

const isProgramExpired = (program) => {
  const diffInDays = differenceInDays(new Date(program?.endDate), new Date());
  return diffInDays < 0;
};

function DonateButton(props) {
  const {
    variant = 'filled',
    nonProfit,
    amount = '',
    post = null,
    withIcon = true,
    ...rest
  } = props;

  const user = useUserProfile();
  const isLoggedIn = useHasUserSession();
  const setShowDonateDialog = useDonateDialogStore((state) => state.setOpen);
  let disabled = !nonProfit || !nonProfit.canReceiveDonations;

  let isCountryNotSame =
    isLoggedIn && getUserCountry(user) !== nonProfit?.country;

  const hasNoDonationUrl = !nonProfit?.donation_url;

  const hasGiftOfGivingAccess =
    hasAppAccess(AppPreferenceItem.GiftOfGiving) &&
    user?.giftOfGiving &&
    !isProgramExpired(user?.giftOfGiving?.program);

  const cannotDonate = hasNoDonationUrl && !hasGiftOfGivingAccess;

  disabled = disabled || isCountryNotSame || cannotDonate;

  const handleDonate = () => {
    if (!disabled) {
      setShowDonateDialog(true, {
        nonProfit,
        amount,
        post,
      });
    }
  };

  const getDisabledTooltipText = () => {
    if (nonProfit.giving_fund_status === 'ON_HOLD') {
      return `This organization is currently not in compliance with California regulations – please check back later for updates on their status`;
    } else if (isCountryNotSame) {
      return `We only support donations to nonprofits within your own country at this time`;
    } else {
      return `Donations coming soon`;
    }
  };

  if (disabled) {
    return (
      <Tooltip content={getDisabledTooltipText()} className={`w-[300px]`}>
        <Button
          variant={variant}
          color="primary"
          disabled
          role="button"
          as="div">
          <div className="flex items-center gap-1">
            <span>Donate</span>
            {withIcon && <HiHeart />}
          </div>
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button
      variant={variant}
      color="primary"
      onClick={handleDonate}
      className="flex items-center gap-1">
      <div className="flex items-center gap-1">
        <span>Donate</span>
        {withIcon && <HiHeart />}
      </div>
    </Button>
  );
}

export default DonateButton;
